import { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { ShieldCheck, Loader2, ArrowRight, Eye, EyeOff, CheckCircle2, XCircle } from 'lucide-react';
import { apiClient, useNotificationStore } from '@dukene/shared';

export default function AcceptInvitation() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const navigate = useNavigate();
    const { addNotification } = useNotificationStore();

    const [organizationName, setOrganizationName] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [status, setStatus] = useState<'form' | 'success' | 'error'>('form');
    const [error, setError] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (organizationName.trim().length < 2) {
            addNotification({ message: 'Organization name must be at least 2 characters', type: 'error' });
            return;
        }

        if (password !== confirmPassword) {
            addNotification({ message: 'Passwords do not match', type: 'error' });
            return;
        }

        if (password.length < 6) {
            addNotification({ message: 'Password must be at least 6 characters', type: 'error' });
            return;
        }

        setIsSubmitting(true);
        setError('');

        try {
            await apiClient.post('/auth/accept-invitation', {
                token,
                password,
                organizationName: organizationName.trim(),
            });
            setStatus('success');
            addNotification({ message: 'Account set up successfully!', type: 'success' });
        } catch (err: any) {
            const msg = err.response?.data?.error?.message || err.message || 'Failed to accept invitation. Link might be expired.';
            setError(msg);
            setStatus('error');
            addNotification({ message: msg, type: 'error' });
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!token) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
                <div className="text-center p-8 bg-white rounded-2xl shadow-sm border border-gray-100 max-w-md w-full">
                    <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Invalid Link</h2>
                    <p className="text-gray-600 mb-6">The invitation link is missing or malformed. Please contact your store owner.</p>
                    <Link to="/login" className="inline-flex items-center gap-2 text-emerald font-semibold hover:underline">
                        Go to Login <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </div>
        );
    }

    if (status === 'success') {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
                <div className="text-center p-10 bg-white rounded-3xl shadow-premium border border-gray-100 max-w-md w-full">
                    <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6">
                        <CheckCircle2 className="w-10 h-10 text-green-600" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-4 tracking-tight">You're All Set!</h2>

                    {window.innerWidth < 1024 ? (
                        <>
                            <p className="text-gray-500 mb-8 leading-relaxed font-light">
                                Your account has been set up successfully. Since you're on a mobile device, please head back to your computer to manage your store with the full dashboard experience.
                            </p>
                            <div className="space-y-4">
                                <div className="p-5 bg-gray-50 rounded-2xl border border-gray-100 text-left">
                                    <p className="text-sm font-semibold text-gray-900 mb-1">Get the Merchant App</p>
                                    <p className="text-xs text-gray-500 mb-4">Manage your store on the go with our dedicated mobile app.</p>
                                    <a
                                        href="https://play.google.com/store/apps/details?id=com.dukene.merchant"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 px-6 h-11 bg-black text-white rounded-xl font-bold text-sm transition-transform active:scale-95 shadow-lg shadow-black/10"
                                    >
                                        Download for Android
                                    </a>
                                </div>
                                <div className="text-center">
                                    <p className="text-xs text-gray-400 mb-2 font-light">Or continue to</p>
                                    <button
                                        onClick={() => navigate('/login')}
                                        className="text-sm text-emerald font-bold hover:underline decoration-2 underline-offset-4"
                                    >
                                        Sign In on this device
                                    </button>
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <p className="text-gray-600 mb-10 leading-relaxed font-light font-outfit">
                                Your account has been set up successfully. You can now log into the merchant dashboard with your email and new password.
                            </p>
                            <button
                                onClick={() => navigate('/login')}
                                className="w-full h-14 bg-black hover:bg-gray-900 text-white font-bold rounded-2xl transition-all flex items-center justify-center gap-2 group shadow-xl shadow-black/10"
                            >
                                Sign In Now
                                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                            </button>
                        </>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex bg-white font-outfit">
            {/* Left Side - Branding */}
            <div className="hidden lg:flex lg:w-1/2 bg-white border-r border-gray-100 relative">
                <div className="flex flex-col justify-between w-full p-16 xl:p-24">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white border border-gray-200 rounded-lg flex items-center justify-center shadow-lg shadow-black/20">
              <img src="/favicon.png" alt="Logo" className="w-full h-full object-contain p-0.5" />
            </div>
                        <div>
                            <h1 className="text-xl font-bold text-black tracking-tight">Dukene</h1>
                            <p className="text-xs text-gray-500 font-medium tracking-wide">MERCHANTS</p>
                        </div>
                    </div>

                    <div className="space-y-16">
                        <div>
                            <h2 className="text-6xl font-light text-black mb-6 leading-tight tracking-tight">
                                Join the
                                <br />
                                Team
                            </h2>
                            <div className="w-16 h-1 bg-black mb-8 rounded-full"></div>
                            <p className="text-xl text-gray-600 leading-relaxed font-light">
                                You've been invited to help manage a store on Dukene. Create your secure password to get started.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 gap-8 pt-8">
                            <div className="flex items-start gap-4">
                                <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center shrink-0">
                                    <ShieldCheck className="w-5 h-5 text-green-600" />
                                </div>
                                <div>
                                    <h4 className="font-semibold text-gray-900">Secure Access</h4>
                                    <p className="text-sm text-gray-500">Your role comes with specific permissions granted by the store owner.</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-8 text-xs text-gray-400 font-light">
                        <span>© 2024 Dukene</span>
                        <span className="w-1 h-1 rounded-full bg-gray-300"></span>
                        <span>v2.0 Beta</span>
                    </div>
                </div>
            </div>

            {/* Right Side - Form */}
            <div className="flex-1 flex items-center justify-center bg-gray-50/30 px-8 lg:px-20 xl:px-32 py-12">
                <div className="w-full max-w-md">
                    {/* Mobile Logo */}
                    <div className="lg:hidden flex items-center justify-center gap-3 mb-12">
                        <div className="w-10 h-10 bg-white border border-gray-200 rounded-lg flex items-center justify-center shadow-lg transform rotate-3">
              <img src="/favicon.png" alt="Logo" className="w-full h-full object-contain p-0.5" />
            </div>
                        <div className="text-left font-outfit">
                            <h1 className="text-xl font-bold text-black leading-tight">Dukene</h1>
                            <p className="text-[10px] text-gray-500 font-bold uppercase tracking-widest">Merchants</p>
                        </div>
                    </div>

                    <div className="bg-white p-8 xl:p-10 rounded-3xl shadow-premium border border-white relative overflow-hidden">
                        <div className="relative z-10">
                            <div className="mb-10 text-center lg:text-left">
                                <h3 className="text-3xl font-bold text-gray-900 mb-2 tracking-tight">Set Up Your Account</h3>
                                <p className="text-gray-500 font-light">Create your organization and secure your account.</p>
                            </div>

                            {status === 'error' && (
                                <div className="mb-8 p-4 bg-red-50 border border-red-100 rounded-2xl flex items-start gap-3">
                                    <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
                                    <p className="text-sm text-red-700 font-medium">{error}</p>
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div className="space-y-2">
                                    <label className="text-sm font-semibold text-gray-700 ml-1">
                                        Organization Name
                                    </label>
                                    <input
                                        type="text"
                                        required
                                        minLength={2}
                                        autoComplete="organization"
                                        value={organizationName}
                                        onChange={(e) => setOrganizationName(e.target.value)}
                                        className="w-full h-14 px-5 bg-gray-50 border-gray-200 border rounded-2xl focus:ring-4 focus:ring-emerald/10 focus:border-emerald outline-none transition-all hover:border-gray-300 font-medium"
                                        placeholder="Your organization name"
                                    />
                                    <p className="text-[10px] text-gray-400 ml-1">
                                        This is your personal organization on Dukene.
                                    </p>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-sm font-semibold text-gray-700 ml-1">New Password</label>
                                    <div className="relative group">
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            autoComplete="new-password"
                                            required
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            className="w-full h-14 px-5 pr-12 bg-gray-50 border-gray-200 border rounded-2xl focus:ring-4 focus:ring-emerald/10 focus:border-emerald outline-none transition-all group-hover:border-gray-300 font-medium"
                                            placeholder="••••••••"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                        >
                                            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                        </button>
                                    </div>
                                    <p className="text-[10px] text-gray-400 ml-1">Minimum 6 characters required.</p>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-sm font-semibold text-gray-700 ml-1">Confirm Password</label>
                                    <div className="relative group">
                                        <input
                                            type={showConfirmPassword ? 'text' : 'password'}
                                            autoComplete="new-password"
                                            required
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            className="w-full h-14 px-5 pr-12 bg-gray-50 border-gray-200 border rounded-2xl focus:ring-4 focus:ring-emerald/10 focus:border-emerald outline-none transition-all group-hover:border-gray-300 font-medium"
                                            placeholder="••••••••"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            className="absolute right-5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                        >
                                            {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                        </button>
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="w-full h-14 bg-black hover:bg-gray-900 disabled:bg-gray-400 text-white font-bold rounded-2xl transition-all shadow-xl shadow-black/10 flex items-center justify-center gap-3 overflow-hidden relative group"
                                >
                                    {isSubmitting ? (
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                    ) : (
                                        <>
                                            <span className="relative z-10">Set Password & Join Store</span>
                                            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform relative z-10" />
                                        </>
                                    )}
                                </button>
                            </form>
                        </div>

                        {/* Background decoration */}
                        <div className="absolute top-0 right-0 -mr-20 -mt-20 w-40 h-40 bg-emerald/5 rounded-full blur-3xl"></div>
                        <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-40 h-40 bg-blue-500/5 rounded-full blur-3xl"></div>
                    </div>

                    <p className="mt-12 text-center text-gray-400 font-light text-sm">
                        Already have an account?{' '}
                        <button onClick={() => navigate('/login')} className="text-emerald font-semibold hover:underline decoration-2 underline-offset-4">
                            Log In
                        </button>
                    </p>
                </div>
            </div>
        </div>
    );
}
